import * as LookupServices from "../../utils/service-vehicles/LookupServices";
import { VehicleOption, ConditionLookupTables, MaxiTaxiLookUpType, ApiVehicleOption, TaxiServiceId } from './Redux/ConditionEntities';
import { ServiceInfo, ServiceKind } from "../../utils/service-vehicles/ServiceMetadata";
import services from '../../utils/service-vehicles/ServiceMetadata';
import { FeatureFlags } from "../../Config/FeatureFlags";
import { Config } from "../../Config/Config";
import { Brand } from "../../Config/Entities/Brand";
import { GetMyBrand } from '../../Config/MyAppConfig';

/* This is currently used for both V1 and V2 Booking API versions. */
export function ParseVehicleConditionHelper(conditions: ApiVehicleOption[]) : ConditionLookupTables {
    var ConditionList: VehicleOption[] = [], MaxiTaxiLookUp: MaxiTaxiLookUpType = { };
    var hasWheelChair: boolean = false, hasMaxiTaxi: boolean = false, wheelChairPlaceHolder: ApiVehicleOption | undefined = undefined;
    // V2: Always use generic "any" as Next Available (anySA doesn't exist in V2)
    // V1: Use anySA if limo is present (legacy behavior)
    const isV2 = conditions.every(v => v.ApiVersion === 2);
    const nextAvailable = isV2 
        ? services.any  
        : (conditions.some(v => /limo/i.test(v['Name'])) ? services.anySA : services.any);
    ConditionList.push(MakeVehicleFromService(nextAvailable));

    for (var condition of conditions) {
        // Use appropriate lookup based on API version
        const service = condition.ApiVersion === 2 
            ? LookupServices.byTaxiServiceId(condition.ApiId)
            : LookupServices.byConditionId(condition.ApiId);
        // Skip "any" and "anySA" as we manually add NextAvailable at the top
        if (service === services.any || service === services.anySA) continue;

        if (service === services.wheelchair) {
            // V1 API: Apply MaxSeat validation (needed for V1 logic)
            if (condition.ApiVersion === 1) {
                // Allow wheelchair bookings only if the maximum number of seats is less than or equal to the configured value.
                const currentMaxSeat = wheelChairPlaceHolder?.MaxSeat ?? 0;
                if (condition.MaxSeat >= currentMaxSeat && condition.MaxSeat <= Config.WheelchairMaxSeat) {
                    hasWheelChair = true;
                    wheelChairPlaceHolder = condition;
                }
            }
            // V2 API: Always include if returned by ServiceOfferings (no seat validation needed)
            // V2 wheelchair will have MaxSeat = 0 when VariableCapacity is null
            else if (condition.ApiVersion === 2) {
                hasWheelChair = true;
                wheelChairPlaceHolder = condition;
            }
        }
        else if (service === services.maxi) {
            hasMaxiTaxi = true;
            
            // V2 API returns only max capacity - generate entries for all seat counts
            if (condition.ApiVersion === 2) {
                const maxSeats = condition.MaxSeat;
                // Generate entries from 1 to maxSeats to match V1 behavior
                for (let seats = 1; seats <= maxSeats; seats++) {
                    MaxiTaxiLookUp[seats] = {
                        ...condition,
                        MaxSeat: seats,
                        Description: `up to ${seats} passenger${seats !== 1 ? 's' : ''}`
                    };
                }
            } else {
                // V1 API returns separate entries for each seat count
                MaxiTaxiLookUp[condition.MaxSeat] = condition;
            }
        }
        else {
            ConditionList.push({ Service: service, ApiVehicle : condition }); 
        }
    }

    /** Add wheelchair & MAXI TAXI*/
    if (hasWheelChair === true) { 
        ConditionList.push(MakeVehicleFromService(services.wheelchair, wheelChairPlaceHolder)); 
    }
    if (hasMaxiTaxi === true) { 
        ConditionList.push(MakeVehicleFromService(services.maxi)); 
    }

    const brand = GetMyBrand();
    
    ConditionList = filterConditionsByBrand(brand, ConditionList);

    // force the items into a standard ordering.
    ConditionList.sort((a, b) => a.Service.StandardSortOrder - b.Service.StandardSortOrder);

    return {ConditionList: ConditionList, MaxiTaxiLookUp: MaxiTaxiLookUp};
};

/**
 * Filter out vehicles that don't support fixed fare when Fixed Price is selected.
 * Only applies to V2 API vehicles that have IsFixedFareAvailable flag.
 */
export function FilterByFixedFareAvailability(vehicles: VehicleOption[], isPriceGuaranteeSelected: boolean): VehicleOption[] {
    // If Fixed Price is not selected, show all vehicles
    if (!isPriceGuaranteeSelected) return vehicles;
    
    // If Fixed Price is selected, filter out V2 vehicles that don't support it
    return vehicles.filter(vehicle => {
        // V1 vehicles don't have IsFixedFareAvailable flag, always include them
        if (!vehicle.ApiVehicle || vehicle.ApiVehicle.ApiVersion !== 2) return true;
        
        // V2 vehicles: only include if IsFixedFareAvailable is true or undefined (backward compat)
        return vehicle.ApiVehicle.IsFixedFareAvailable !== false;
    });
}

function filterConditionsByBrand(brand: Brand, conditionList: VehicleOption[]): VehicleOption[] {
    switch (brand) {
        case Brand.SilverService:
            return conditionList.filter(
                c =>
                    c.Service !== services.parcel &&
                    c.Service !== services.sedan &&
                    c.Service !== services.anySA &&
                    c.Service !== services.wagon &&
                    c.Service !== services.any
            );
        default:
            return conditionList.filter(
                c =>
                    c.Service !== services.silverexec &&
                    c.Service !== services.silverexecxl
            );       
    }
}

function MakeVehicleFromService(service: ServiceInfo, wheelChairCondition?: ApiVehicleOption): VehicleOption {
    if (wheelChairCondition) {
        return { Service: service, ApiVehicle: wheelChairCondition };
    }
    
    // For BookingApiV2, include ApiVehicle for NextAvailable (TaxiServiceId 1)
    if (FeatureFlags.BookingApiV2 && (service.kind === ServiceKind.NextAvailable || service.kind === ServiceKind.NextAvailableSouthAustralia)) {
        // Both NextAvailable and NextAvailableSouthAustralia map to TaxiServiceId.NextAvailable (1)
        return {
            Service: service,
            ApiVehicle: {
                ApiVersion: 2,
                ApiId: TaxiServiceId.NextAvailable,
                Name: service.displayName,
                MaxSeat: 4,
                Description: "Next available"
            }
        };
    }
    
    return { Service: service, RequiresPaxCount: !FeatureFlags.GlobalBooker && service.isMaxiTaxi };
}
